import { z } from "zod";

/** Matrix catalog ids → platform / broker provider ids. */
export const MATRIX_TO_PLATFORM: Record<string, string> = {
	ga4: "ga4",
	google_ads: "google_ads",
	gsc: "google_search_console",
	gtm: "gtm",
	gbp: "gbp",
	google_drive: "google_drive",
	google_photos: "google_photos",
	canva: "canva",
	figma: "figma",
	dropbox: "dropbox",
	github: "github",
	vercel: "vercel",
	cloudflare: "cloudflare",
	godaddy: "godaddy",
	linkedin: "linkedin",
	linkedin_ads: "linkedin_ads",
	x: "x",
	shopify: "shopify",
	wordpress: "wordpress",
	pinterest: "pinterest",
	email: "gmail",
	wix: "wix",
	webflow: "webflow",
	squarespace: "squarespace",
};

export const PLATFORM_TO_MATRIX: Record<string, string> = {
	ga4: "ga4",
	google_ads: "google_ads",
	google_search_console: "gsc",
	gtm: "gtm",
	gbp: "gbp",
	google_drive: "google_drive",
	google_photos: "google_photos",
	canva: "canva",
	figma: "figma",
	dropbox: "dropbox",
	github: "github",
	vercel: "vercel",
	cloudflare: "cloudflare",
	godaddy: "godaddy",
	linkedin: "linkedin",
	linkedin_ads: "linkedin_ads",
	x: "x",
	shopify: "shopify",
	wordpress: "wordpress",
	pinterest: "pinterest",
	gmail: "email",
	wix: "wix",
	webflow: "webflow",
	squarespace: "squarespace",
};

/** Providers Matrix can Connect today (rest stay Soon). */
export const LIVE_CONNECT_PROVIDERS = new Set([
	"ga4",
	"google_ads",
	"gsc",
	"gtm",
	"gbp",
	"google_drive",
	"google_photos",
	"canva",
	"figma",
	"dropbox",
	"github",
	"vercel",
	"cloudflare",
	"godaddy",
	"linkedin",
	"linkedin_ads",
	"x",
	"shopify",
	"wordpress",
	"pinterest",
	"email",
	"wix",
	"webflow",
	"squarespace",
]);

const healthSchema = z.enum(["healthy", "degraded", "revoked"]);

const providerRowSchema = z.object({
	id: z.string(),
	label: z.string(),
	availableScopes: z.array(z.string()),
	configured: z.boolean(),
	connection: z
		.object({
			health: healthSchema,
			grantedScopes: z.array(z.string()),
			externalAccountId: z.string().nullable(),
			connectedAt: z.string(),
			lastCheckedAt: z.string().nullable(),
		})
		.nullable(),
});

const listSchema = z.object({
	providers: z.array(providerRowSchema),
});

export type PlatformConnection = z.infer<typeof providerRowSchema>;

const ga4PropertySchema = z.object({
	id: z.string(),
	displayName: z.string(),
	accountName: z.string(),
});

const adsCustomerSchema = z.object({
	id: z.string(),
	customerId: z.string(),
	displayName: z.string(),
	isManager: z.boolean(),
	managerName: z.string().optional(),
	enabled: z.boolean().optional(),
});

const adsPortfolioAccountSchema = z.object({
	id: z.string(),
	externalAccountId: z.string(),
	displayName: z.string(),
	enabled: z.boolean(),
	meta: z.record(z.string(), z.unknown()),
	syncedAt: z.string(),
});

const gscSiteSchema = z.object({
	id: z.string(),
	permissionLevel: z.string(),
});

export type Ga4Property = z.infer<typeof ga4PropertySchema>;
export type AdsCustomer = z.infer<typeof adsCustomerSchema>;
export type PortfolioAccount = z.infer<typeof adsPortfolioAccountSchema>;
/** @deprecated Use PortfolioAccount */
export type AdsPortfolioAccount = PortfolioAccount;
export type GscSite = z.infer<typeof gscSiteSchema>;

async function readJson(response: Response): Promise<unknown> {
	return response.json() as Promise<unknown>;
}

/**
 * Mint / refresh the platform `mairko_session` cookie from a Clerk JWT.
 * Must be called with credentials so Set-Cookie lands on the Matrix origin
 * (Vite proxies /api → backend).
 */
export async function ensurePlatformSession(input: {
	getToken: () => Promise<string | null>;
	orgName?: string | null;
	orgId?: string | null;
	/** Abort in-flight token + session mint (reload / org switch). */
	signal?: AbortSignal;
	/** Wall-clock limit; local wrangler cold starts can take tens of seconds. */
	timeoutMs?: number;
}): Promise<
	| { ok: true; tenantId: string; tenantName: string; clerkOrgId: string | null }
	| { ok: false; error: string }
> {
	const timeoutMs = input.timeoutMs ?? 20_000;
	const controller = new AbortController();
	const onAbort = () => controller.abort();
	input.signal?.addEventListener("abort", onAbort, { once: true });
	const timer = setTimeout(() => controller.abort(), timeoutMs);

	try {
		if (controller.signal.aborted) {
			return { ok: false, error: "session_timeout" };
		}

		const token = await Promise.race([
			input.getToken(),
			new Promise<null>((_, reject) => {
				controller.signal.addEventListener(
					"abort",
					() => reject(new DOMException("Aborted", "AbortError")),
					{ once: true },
				);
			}),
		]);
		if (!token) return { ok: false, error: "missing_clerk_token" };

		const response = await fetch("/api/session/clerk", {
			method: "POST",
			credentials: "include",
			signal: controller.signal,
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				orgName: input.orgName?.trim() || undefined,
				orgId: input.orgId?.trim() || undefined,
			}),
		});

		if (!response.ok) {
			const body = await readJson(response).catch(() => null);
			const error =
				body &&
				typeof body === "object" &&
				"error" in body &&
				typeof body.error === "string"
					? body.error
					: `session_${response.status}`;
			return { ok: false, error };
		}

		const parsed = z
			.object({
				ok: z.literal(true),
				tenantId: z.string(),
				tenantName: z.string().optional(),
				clerkOrgId: z.string().nullable().optional(),
			})
			.safeParse(await readJson(response));
		if (!parsed.success) return { ok: false, error: "invalid_session_response" };
		return {
			ok: true,
			tenantId: parsed.data.tenantId,
			tenantName: parsed.data.tenantName ?? "Workspace",
			clerkOrgId: parsed.data.clerkOrgId ?? null,
		};
	} catch (cause) {
		if (
			controller.signal.aborted ||
			(cause instanceof DOMException && cause.name === "AbortError")
		) {
			return {
				ok: false,
				error: input.signal?.aborted ? "session_aborted" : "session_timeout",
			};
		}
		return { ok: false, error: "session_network_error" };
	} finally {
		clearTimeout(timer);
		input.signal?.removeEventListener("abort", onAbort);
	}
}

export async function listConnections(): Promise<
	{ ok: true; providers: PlatformConnection[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections", { credentials: "include" });
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (!response.ok) return { ok: false, error: `list_${response.status}` };

	const parsed = listSchema.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_list" };
	return { ok: true, providers: parsed.data.providers };
}

export function connectHref(
	matrixId: string,
	opts?: { shop?: string },
): string {
	const platformId = MATRIX_TO_PLATFORM[matrixId] ?? matrixId;
	const returnTo = encodeURIComponent(
		`/?matrixConnector=${encodeURIComponent(matrixId)}`,
	);
	if (platformId === "google_ads") {
		return `/oauth/google_ads/start?scopes=${encodeURIComponent("google_ads.read,google_ads.write")}&returnTo=${returnTo}`;
	}
	if (platformId === "gmail") {
		// Email channel needs send; request read + write on first Connect.
		return `/oauth/gmail/start?scopes=${encodeURIComponent("gmail.read,gmail.write")}&returnTo=${returnTo}`;
	}
	if (platformId === "shopify") {
		const shop = opts?.shop?.trim();
		if (!shop) {
			return `/oauth/shopify/start?returnTo=${returnTo}`;
		}
		return `/oauth/shopify/start?shop=${encodeURIComponent(shop)}&returnTo=${returnTo}`;
	}
	return `/oauth/${platformId}/start?returnTo=${returnTo}`;
}

export async function disconnectProvider(
	matrixId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const platformId = MATRIX_TO_PLATFORM[matrixId] ?? matrixId;
	const response = await fetch(`/api/connections/${platformId}/disconnect`, {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) return { ok: false, error: `disconnect_${response.status}` };
	return { ok: true };
}

export async function listGa4Properties(): Promise<
	{ ok: true; properties: Ga4Property[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/ga4/properties", {
		credentials: "include",
	});
	if (!response.ok) {
		return {
			ok: false,
			error:
				response.status === 409
					? "reconnect_required"
					: `properties_${response.status}`,
		};
	}
	const parsed = z
		.object({ properties: z.array(ga4PropertySchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_properties" };
	return { ok: true, properties: parsed.data.properties };
}

export async function bindGa4Property(
	propertyId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/ga4/property", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ propertyId }),
	});
	if (!response.ok) return { ok: false, error: `bind_${response.status}` };
	return { ok: true };
}

export async function smokeGa4(): Promise<
	| {
			ok: true;
			totalSessions: number;
			propertyId: string;
			startDate: string;
			endDate: string;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/ga4/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const error =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `smoke_${response.status}`;
		return { ok: false, error };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			totalSessions: z.number(),
			propertyId: z.string(),
			startDate: z.string(),
			endDate: z.string(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		totalSessions: parsed.data.totalSessions,
		propertyId: parsed.data.propertyId,
		startDate: parsed.data.startDate,
		endDate: parsed.data.endDate,
	};
}

export async function listAdsCustomers(): Promise<
	{ ok: true; customers: AdsCustomer[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/google_ads/customers", {
		credentials: "include",
	});
	if (!response.ok) {
		return {
			ok: false,
			error:
				response.status === 503
					? "developer_token_missing"
					: response.status === 409
						? "reconnect_required"
						: `customers_${response.status}`,
		};
	}
	const parsed = z
		.object({ customers: z.array(adsCustomerSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_customers" };
	return { ok: true, customers: parsed.data.customers };
}

export async function listAdsPortfolio(): Promise<
	| { ok: true; accounts: PortfolioAccount[]; synced: boolean }
	| { ok: false; error: string }
> {
	return listProviderPortfolio("google_ads");
}

export async function refreshAdsPortfolio(): Promise<
	{ ok: true; accounts: PortfolioAccount[] } | { ok: false; error: string }
> {
	return refreshProviderPortfolio("google_ads");
}

export async function listProviderPortfolio(
	provider: string,
): Promise<
	| { ok: true; accounts: PortfolioAccount[]; synced: boolean }
	| { ok: false; error: string }
> {
	const response = await fetch(
		`/api/connections/${encodeURIComponent(provider)}/portfolio`,
		{ credentials: "include" },
	);
	if (!response.ok) {
		return { ok: false, error: `portfolio_${response.status}` };
	}
	const parsed = z
		.object({
			accounts: z.array(adsPortfolioAccountSchema),
			synced: z.boolean().optional(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_portfolio" };
	return {
		ok: true,
		accounts: parsed.data.accounts,
		synced: parsed.data.synced ?? true,
	};
}

export async function refreshProviderPortfolio(
	provider: string,
): Promise<
	{ ok: true; accounts: PortfolioAccount[] } | { ok: false; error: string }
> {
	const response = await fetch(
		`/api/connections/${encodeURIComponent(provider)}/portfolio/refresh`,
		{ method: "POST", credentials: "include" },
	);
	if (!response.ok) {
		return {
			ok: false,
			error:
				response.status === 503
					? "developer_token_missing"
					: response.status === 409
						? "reconnect_required"
						: `refresh_${response.status}`,
		};
	}
	const parsed = z
		.object({
			ok: z.literal(true).optional(),
			accounts: z.array(adsPortfolioAccountSchema),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_refresh" };
	return { ok: true, accounts: parsed.data.accounts };
}

/** @deprecated Optional legacy focus — portfolio access does not require this. */
export async function bindAdsCustomer(
	customerId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/google_ads/customer", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ customerId }),
	});
	if (!response.ok) return { ok: false, error: `bind_${response.status}` };
	return { ok: true };
}

export async function listGscSites(): Promise<
	{ ok: true; sites: GscSite[] } | { ok: false; error: string }
> {
	const response = await fetch(
		"/api/connections/google_search_console/sites",
		{ credentials: "include" },
	);
	if (!response.ok) {
		return {
			ok: false,
			error:
				response.status === 409
					? "reconnect_required"
					: `sites_${response.status}`,
		};
	}
	const parsed = z
		.object({ sites: z.array(gscSiteSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_sites" };
	return { ok: true, sites: parsed.data.sites };
}

export async function bindGscSite(
	siteUrl: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch(
		"/api/connections/google_search_console/site",
		{
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ siteUrl }),
		},
	);
	if (!response.ok) return { ok: false, error: `bind_${response.status}` };
	return { ok: true };
}

const gtmContainerSchema = z.object({
	id: z.string(),
	displayName: z.string(),
	accountName: z.string(),
	publicId: z.string(),
});

const gbpLocationSchema = z.object({
	id: z.string(),
	displayName: z.string(),
	accountName: z.string(),
});

export type GtmContainer = z.infer<typeof gtmContainerSchema>;
export type GbpLocation = z.infer<typeof gbpLocationSchema>;

export async function listGtmContainers(): Promise<
	{ ok: true; containers: GtmContainer[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/gtm/containers", {
		credentials: "include",
	});
	if (!response.ok) {
		return {
			ok: false,
			error:
				response.status === 409
					? "reconnect_required"
					: `containers_${response.status}`,
		};
	}
	const parsed = z
		.object({ containers: z.array(gtmContainerSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_containers" };
	return { ok: true, containers: parsed.data.containers };
}

export async function bindGtmContainer(
	containerId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/gtm/container", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ containerId }),
	});
	if (!response.ok) return { ok: false, error: `bind_${response.status}` };
	return { ok: true };
}

export async function smokeGtm(): Promise<
	| { ok: true; containerId: string; name: string | null; publicId: string | null }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/gtm/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const error =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `smoke_${response.status}`;
		return { ok: false, error };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			containerId: z.string(),
			name: z.string().nullable(),
			publicId: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		containerId: parsed.data.containerId,
		name: parsed.data.name,
		publicId: parsed.data.publicId,
	};
}

export async function listGbpLocations(): Promise<
	{ ok: true; locations: GbpLocation[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/gbp/locations", {
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		if (response.status === 409) return { ok: false, error: "reconnect_required" };
		if (
			body &&
			typeof body === "object" &&
			"error" in body &&
			body.error === "provider_error" &&
			"status" in body &&
			typeof body.status === "number"
		) {
			const hint =
				"hint" in body && typeof body.hint === "string" ? ` — ${body.hint}` : "";
			return {
				ok: false,
				error: `Google Business Profile API ${body.status}${hint}`,
			};
		}
		return { ok: false, error: `locations_${response.status}` };
	}
	const parsed = z
		.object({ locations: z.array(gbpLocationSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_locations" };
	return { ok: true, locations: parsed.data.locations };
}

export async function bindGbpLocation(
	locationId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/gbp/location", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ locationId }),
	});
	if (!response.ok) return { ok: false, error: `bind_${response.status}` };
	return { ok: true };
}

export async function smokeGbp(): Promise<
	| { ok: true; locationId: string; title: string | null }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/gbp/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const error =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `smoke_${response.status}`;
		return { ok: false, error };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			locationId: z.string(),
			title: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		locationId: parsed.data.locationId,
		title: parsed.data.title,
	};
}

const githubRepoSchema = z.object({
	id: z.string(),
	fullName: z.string(),
	private: z.boolean(),
	defaultBranch: z.string(),
	htmlUrl: z.string(),
});

export type GithubRepo = z.infer<typeof githubRepoSchema>;

export async function listGithubRepos(): Promise<
	{ ok: true; repos: GithubRepo[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/github/repos", {
		credentials: "include",
	});
	if (!response.ok) {
		return {
			ok: false,
			error:
				response.status === 409
					? "reconnect_required"
					: `repos_${response.status}`,
		};
	}
	const parsed = z
		.object({ repos: z.array(githubRepoSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_repos" };
	return { ok: true, repos: parsed.data.repos };
}

export async function bindGithubRepo(
	repoId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/github/repo", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ repoId }),
	});
	if (!response.ok) return { ok: false, error: `bind_${response.status}` };
	return { ok: true };
}

export async function smokeGithub(): Promise<
	| {
			ok: true;
			repoId: string;
			fullName: string;
			defaultBranch: string;
			private: boolean;
			htmlUrl: string;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/github/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const error =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `smoke_${response.status}`;
		return { ok: false, error };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			repoId: z.string(),
			fullName: z.string(),
			defaultBranch: z.string(),
			private: z.boolean(),
			htmlUrl: z.string(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		repoId: parsed.data.repoId,
		fullName: parsed.data.fullName,
		defaultBranch: parsed.data.defaultBranch,
		private: parsed.data.private,
		htmlUrl: parsed.data.htmlUrl,
	};
}

const vercelProjectSchema = z.object({
	id: z.string(),
	projectId: z.string(),
	name: z.string(),
	framework: z.string().nullable(),
	productionUrl: z.string().nullable(),
	teamId: z.string().nullable(),
	teamSlug: z.string().nullable(),
});

export type VercelProject = z.infer<typeof vercelProjectSchema>;

export async function listVercelProjects(): Promise<
	{ ok: true; projects: VercelProject[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/vercel/projects", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `list_${response.status}` };

	const parsed = z
		.object({ projects: z.array(vercelProjectSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_list" };
	return { ok: true, projects: parsed.data.projects };
}

export async function bindVercelProject(
	projectId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/vercel/project", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ projectId }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokeVercel(): Promise<
	| {
			ok: true;
			projectId: string;
			name: string;
			framework: string | null;
			productionUrl: string | null;
			teamSlug: string | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/vercel/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			projectId: z.string(),
			name: z.string(),
			framework: z.string().nullable(),
			productionUrl: z.string().nullable(),
			teamSlug: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		projectId: parsed.data.projectId,
		name: parsed.data.name,
		framework: parsed.data.framework,
		productionUrl: parsed.data.productionUrl,
		teamSlug: parsed.data.teamSlug,
	};
}

const cloudflareZoneSchema = z.object({
	id: z.string(),
	name: z.string(),
	status: z.string(),
	accountId: z.string().nullable(),
	accountName: z.string().nullable(),
});

export type CloudflareZone = z.infer<typeof cloudflareZoneSchema>;

export async function listCloudflareZones(): Promise<
	{ ok: true; zones: CloudflareZone[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/cloudflare/zones", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `list_${response.status}` };

	const parsed = z
		.object({ zones: z.array(cloudflareZoneSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_list" };
	return { ok: true, zones: parsed.data.zones };
}

export async function bindCloudflareZone(
	zoneId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/cloudflare/zone", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ zoneId }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokeCloudflare(): Promise<
	| {
			ok: true;
			zoneId: string;
			name: string;
			status: string;
			accountName: string | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/cloudflare/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			zoneId: z.string(),
			name: z.string(),
			status: z.string(),
			accountName: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		zoneId: parsed.data.zoneId,
		name: parsed.data.name,
		status: parsed.data.status,
		accountName: parsed.data.accountName,
	};
}

const godaddyDomainSchema = z.object({
	domain: z.string(),
	domainId: z.number().nullable(),
	status: z.string(),
	expires: z.string().nullable(),
});

export type GodaddyDomain = z.infer<typeof godaddyDomainSchema>;

export async function connectGodaddy(
	pat: string,
): Promise<
	{ ok: true; domainCount: number } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/godaddy/connect", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ pat }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `connect_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({ ok: z.literal(true), domainCount: z.number() })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_connect" };
	return { ok: true, domainCount: parsed.data.domainCount };
}

export async function listGodaddyDomains(): Promise<
	{ ok: true; domains: GodaddyDomain[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/godaddy/domains", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `list_${response.status}` };

	const parsed = z
		.object({ domains: z.array(godaddyDomainSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_list" };
	return { ok: true, domains: parsed.data.domains };
}

export async function bindGodaddyDomain(
	domain: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/godaddy/domain", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ domain }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokeGodaddy(): Promise<
	| {
			ok: true;
			domain: string;
			status: string;
			expires: string | null;
			domainId: number | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/godaddy/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			domain: z.string(),
			status: z.string(),
			expires: z.string().nullable(),
			domainId: z.number().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		domain: parsed.data.domain,
		status: parsed.data.status,
		expires: parsed.data.expires,
		domainId: parsed.data.domainId,
	};
}

const linkedinOrganizationSchema = z.object({
	id: z.string(),
	name: z.string(),
	role: z.string().nullable(),
	vanityName: z.string().nullable(),
});

export type LinkedinOrganization = z.infer<typeof linkedinOrganizationSchema>;

const linkedinAdAccountSchema = z.object({
	id: z.string(),
	name: z.string(),
	status: z.string(),
	type: z.string().nullable(),
	test: z.boolean(),
});

export type LinkedinAdAccount = z.infer<typeof linkedinAdAccountSchema>;

export async function listLinkedinOrganizations(): Promise<
	| { ok: true; organizations: LinkedinOrganization[] }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/linkedin/organizations", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `list_${response.status}` };

	const parsed = z
		.object({ organizations: z.array(linkedinOrganizationSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_list" };
	return { ok: true, organizations: parsed.data.organizations };
}

export async function bindLinkedinOrganization(
	organizationId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/linkedin/organization", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ organizationId }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokeLinkedin(): Promise<
	| {
			ok: true;
			organizationId: string;
			name: string;
			vanityName: string | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/linkedin/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			organizationId: z.string(),
			name: z.string(),
			vanityName: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		organizationId: parsed.data.organizationId,
		name: parsed.data.name,
		vanityName: parsed.data.vanityName,
	};
}

export async function listLinkedinAdAccounts(): Promise<
	| { ok: true; adAccounts: LinkedinAdAccount[] }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/linkedin_ads/ad-accounts", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `list_${response.status}` };

	const parsed = z
		.object({ adAccounts: z.array(linkedinAdAccountSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_list" };
	return { ok: true, adAccounts: parsed.data.adAccounts };
}

export async function bindLinkedinAdAccount(
	adAccountId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/linkedin_ads/ad-account", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ adAccountId }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokeLinkedinAds(): Promise<
	| {
			ok: true;
			adAccountId: string;
			name: string;
			status: string;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/linkedin_ads/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			adAccountId: z.string(),
			name: z.string(),
			status: z.string(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		adAccountId: parsed.data.adAccountId,
		name: parsed.data.name,
		status: parsed.data.status,
	};
}

const xUserSchema = z.object({
	id: z.string(),
	username: z.string(),
	name: z.string(),
});

export type XUser = z.infer<typeof xUserSchema>;

export async function fetchXMe(): Promise<
	{ ok: true; user: XUser } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/x/me", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `me_${response.status}` };

	const parsed = z
		.object({ user: xUserSchema })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_me" };
	return { ok: true, user: parsed.data.user };
}

export async function bindXAccount(
	userId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/x/account", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ userId }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokeX(): Promise<
	| {
			ok: true;
			userId: string;
			username: string;
			name: string;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/x/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			userId: z.string(),
			username: z.string(),
			name: z.string(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		userId: parsed.data.userId,
		username: parsed.data.username,
		name: parsed.data.name,
	};
}

export async function smokeShopify(): Promise<
	| {
			ok: true;
			shop: string;
			name: string;
			domain: string | null;
			currency: string | null;
			planName: string | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/shopify/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			shop: z.string(),
			name: z.string(),
			domain: z.string().nullable(),
			currency: z.string().nullable(),
			planName: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		shop: parsed.data.shop,
		name: parsed.data.name,
		domain: parsed.data.domain,
		currency: parsed.data.currency,
		planName: parsed.data.planName,
	};
}

export async function smokeWordpress(): Promise<
	| {
			ok: true;
			siteId: string;
			name: string;
			url: string;
			jetpack: boolean;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/wordpress/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			siteId: z.string(),
			name: z.string(),
			url: z.string(),
			jetpack: z.boolean(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		siteId: parsed.data.siteId,
		name: parsed.data.name,
		url: parsed.data.url,
		jetpack: parsed.data.jetpack,
	};
}

const wordpressSiteSchema = z.object({
	id: z.string(),
	name: z.string(),
	url: z.string(),
	jetpack: z.boolean(),
	isPrivate: z.boolean(),
});

export type WordpressSite = z.infer<typeof wordpressSiteSchema>;

export async function listWordpressSites(): Promise<
	{ ok: true; sites: WordpressSite[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/wordpress/sites", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `list_${response.status}` };

	const parsed = z
		.object({ sites: z.array(wordpressSiteSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_list" };
	return { ok: true, sites: parsed.data.sites };
}

export async function bindWordpressSite(
	siteId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/wordpress/site", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ siteId }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function fetchPinterestMe(): Promise<
	| {
			ok: true;
			user: {
				username: string;
				accountType: string | null;
				websiteUrl: string | null;
			};
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/pinterest/me", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `me_${response.status}` };

	const parsed = z
		.object({
			user: z.object({
				username: z.string(),
				accountType: z.string().nullable(),
				profileImage: z.string().nullable(),
				websiteUrl: z.string().nullable(),
			}),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_me" };
	return {
		ok: true,
		user: {
			username: parsed.data.user.username,
			accountType: parsed.data.user.accountType,
			websiteUrl: parsed.data.user.websiteUrl,
		},
	};
}

export async function bindPinterestAccount(
	username: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/pinterest/account", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ username }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokePinterest(): Promise<
	| {
			ok: true;
			username: string;
			accountType: string | null;
			websiteUrl: string | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/pinterest/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			username: z.string(),
			accountType: z.string().nullable(),
			websiteUrl: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		username: parsed.data.username,
		accountType: parsed.data.accountType,
		websiteUrl: parsed.data.websiteUrl,
	};
}

export async function fetchGmailMe(): Promise<
	| {
			ok: true;
			profile: {
				emailAddress: string;
				messagesTotal: number | null;
				threadsTotal: number | null;
			};
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/gmail/me", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (response.status === 409) return { ok: false, error: "not_connected" };
	if (!response.ok) return { ok: false, error: `me_${response.status}` };

	const parsed = z
		.object({
			profile: z.object({
				emailAddress: z.string(),
				messagesTotal: z.number().nullable(),
				threadsTotal: z.number().nullable(),
			}),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_me" };
	return { ok: true, profile: parsed.data.profile };
}

export async function bindGmailAddress(
	emailAddress: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/gmail/address", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ emailAddress }),
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokeGmail(): Promise<
	| {
			ok: true;
			emailAddress: string;
			messagesTotal: number | null;
			threadsTotal: number | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/gmail/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (!response.ok) {
		const body = await readJson(response);
		const err =
			body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			emailAddress: z.string(),
			messagesTotal: z.number().nullable(),
			threadsTotal: z.number().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		emailAddress: parsed.data.emailAddress,
		messagesTotal: parsed.data.messagesTotal,
		threadsTotal: parsed.data.threadsTotal,
	};
}

const webflowSiteSchema = z.object({
	id: z.string(),
	displayName: z.string(),
	shortName: z.string().nullable(),
	previewUrl: z.string().nullable(),
});

export type WebflowSite = z.infer<typeof webflowSiteSchema>;

export async function listWebflowSites(): Promise<
	{ ok: true; sites: WebflowSite[] } | { ok: false; error: string }
> {
	const response = await fetch("/api/connections/webflow/sites", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const err =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `sites_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({ sites: z.array(webflowSiteSchema) })
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_sites" };
	return { ok: true, sites: parsed.data.sites };
}

export async function bindWebflowSite(
	siteId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const response = await fetch("/api/connections/webflow/site", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ siteId }),
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const err =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `bind_${response.status}`;
		return { ok: false, error: err };
	}
	return { ok: true };
}

export async function smokeWebflow(): Promise<
	| {
			ok: true;
			siteId: string;
			displayName: string;
			shortName: string | null;
			previewUrl: string | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/webflow/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const err =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			siteId: z.string(),
			displayName: z.string(),
			shortName: z.string().nullable(),
			previewUrl: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		siteId: parsed.data.siteId,
		displayName: parsed.data.displayName,
		shortName: parsed.data.shortName,
		previewUrl: parsed.data.previewUrl,
	};
}

export async function smokeSquarespace(): Promise<
	| {
			ok: true;
			id: string;
			title: string;
			url: string | null;
			siteId: string | null;
			currency: string | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/squarespace/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const err =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			id: z.string(),
			title: z.string(),
			url: z.string().nullable(),
			siteId: z.string().nullable(),
			currency: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		id: parsed.data.id,
		title: parsed.data.title,
		url: parsed.data.url,
		siteId: parsed.data.siteId,
		currency: parsed.data.currency,
	};
}

export async function fetchWixMe(): Promise<
	| {
			ok: true;
			site: {
				siteId: string;
				siteDisplayName: string;
				url: string | null;
			};
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/wix/me", {
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const err =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `me_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			site: z.object({
				siteId: z.string(),
				siteDisplayName: z.string(),
				url: z.string().nullable(),
			}),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_me" };
	return { ok: true, site: parsed.data.site };
}

export async function smokeWix(): Promise<
	| {
			ok: true;
			siteId: string;
			siteDisplayName: string;
			url: string | null;
	  }
	| { ok: false; error: string }
> {
	const response = await fetch("/api/connections/wix/smoke", {
		method: "POST",
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const err =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `smoke_${response.status}`;
		return { ok: false, error: err };
	}
	const parsed = z
		.object({
			ok: z.literal(true),
			siteId: z.string(),
			siteDisplayName: z.string(),
			url: z.string().nullable(),
		})
		.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return {
		ok: true,
		siteId: parsed.data.siteId,
		siteDisplayName: parsed.data.siteDisplayName,
		url: parsed.data.url,
	};
}

async function smokeSimple(
	path: string,
	shape: z.ZodType<{ ok: true } & Record<string, unknown>>,
): Promise<
	| ({ ok: true } & Record<string, unknown>)
	| { ok: false; error: string }
> {
	const response = await fetch(path, {
		method: "POST",
		credentials: "include",
	});
	if (response.status === 401) return { ok: false, error: "unauthorized" };
	if (!response.ok) {
		const body = await readJson(response).catch(() => null);
		const error =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof body.error === "string"
				? body.error
				: `smoke_${response.status}`;
		return { ok: false, error };
	}
	const parsed = shape.safeParse(await readJson(response));
	if (!parsed.success) return { ok: false, error: "invalid_smoke" };
	return parsed.data;
}

export async function smokeGoogleDrive(): Promise<
	| { ok: true; fileCount: number; sampleName: string | null }
	| { ok: false; error: string }
> {
	const result = await smokeSimple(
		"/api/connections/google_drive/smoke",
		z.object({
			ok: z.literal(true),
			fileCount: z.number(),
			sampleName: z.string().nullable(),
		}),
	);
	if (!result.ok) return result;
	return {
		ok: true,
		fileCount: result.fileCount as number,
		sampleName: (result.sampleName as string | null) ?? null,
	};
}

export async function smokeGooglePhotos(): Promise<
	| { ok: true; mediaCount: number; sampleId: string | null }
	| { ok: false; error: string }
> {
	const result = await smokeSimple(
		"/api/connections/google_photos/smoke",
		z.object({
			ok: z.literal(true),
			mediaCount: z.number(),
			sampleId: z.string().nullable(),
		}),
	);
	if (!result.ok) return result;
	return {
		ok: true,
		mediaCount: result.mediaCount as number,
		sampleId: (result.sampleId as string | null) ?? null,
	};
}

export async function smokeCanva(): Promise<
	| { ok: true; designCount: number; sampleTitle: string | null }
	| { ok: false; error: string }
> {
	const result = await smokeSimple(
		"/api/connections/canva/smoke",
		z.object({
			ok: z.literal(true),
			designCount: z.number(),
			sampleTitle: z.string().nullable(),
		}),
	);
	if (!result.ok) return result;
	return {
		ok: true,
		designCount: result.designCount as number,
		sampleTitle: (result.sampleTitle as string | null) ?? null,
	};
}

export async function smokeFigma(): Promise<
	| { ok: true; handle: string; email: string | null }
	| { ok: false; error: string }
> {
	const result = await smokeSimple(
		"/api/connections/figma/smoke",
		z.object({
			ok: z.literal(true),
			handle: z.string(),
			email: z.string().nullable(),
		}),
	);
	if (!result.ok) return result;
	return {
		ok: true,
		handle: result.handle as string,
		email: (result.email as string | null) ?? null,
	};
}

export async function smokeDropbox(): Promise<
	| { ok: true; entryCount: number; sampleName: string | null }
	| { ok: false; error: string }
> {
	const result = await smokeSimple(
		"/api/connections/dropbox/smoke",
		z.object({
			ok: z.literal(true),
			entryCount: z.number(),
			sampleName: z.string().nullable(),
		}),
	);
	if (!result.ok) return result;
	return {
		ok: true,
		entryCount: result.entryCount as number,
		sampleName: (result.sampleName as string | null) ?? null,
	};
}

export function connectionStatusForMatrix(
	providers: PlatformConnection[],
	matrixId: string,
): "connected" | "attention" | "available" | null {
	if (!LIVE_CONNECT_PROVIDERS.has(matrixId)) return null;
	const platformId = MATRIX_TO_PLATFORM[matrixId];
	if (!platformId) return null;
	const row = providers.find((p) => p.id === platformId);
	if (!row?.configured) return "available";
	const conn = row.connection;
	if (!conn || conn.health === "revoked") return "available";
	if (conn.health === "degraded") return "attention";
	return "connected";
}
